// src/components/templates/Layout.jsx
import { useLocation } from "react-router-dom";
import {
  Box,
  HStack,
  Image,
  Flex,
  Icon,
  Text,
  Link,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  MdLockReset,
  MdStorage,
  MdPhonelinkLock,
  MdComputer,
} from "react-icons/md";
import { NavLink } from "react-router-dom";

const CONTAINER_MAX = 700;     // ここで全体の最大幅を定義
const TAB_BAR_HEIGHT = 56;     // 下タブの高さ
const FOOTER_HEIGHT  = 44;     // フッター高さ（目安）

export default function Layout({ children }) {
  const location = useLocation();

  // タブの定義とルートのマッピング / Tab definitions and route mappings
  const tabs = [
    { key: "reset", label: "Reset Credentials", icon: MdLockReset, path: "/reset-credentials" },
    { key: "resource", label: "ReCreate RG", icon: MdStorage, path: "/recreate-rg" },
    { key: "mfa", label: "Reset MFA", icon: MdPhonelinkLock, path: "/reset-mfa" },
    { key: "vm", label: "Full Read Access", icon: MdComputer, path: "/full-read-access" },
  ];

  // base（モバイル）はタブを画面下固定、md以上は上
  const tabPlacement = useBreakpointValue({ base: "bottom", md: "top" });
  const isBottom = tabPlacement === "bottom";

  return (
    <Flex direction="column" minH="100vh" align="center">
      {/* ヘッダー */}
      <Box as="header" px={6} pt={6} w="100%" maxW={`${CONTAINER_MAX}px`}>
        <HStack justify="center" mb={4}>
          <Image src="/LogoBlack.svg" alt="LOG(N) PACIFIC" h="64px" />
        </HStack>
      </Box>

      {/* メイン */}
      <Box
        as="main"
        flex="1"
        px={6}
        w="100%"
        maxW={`${CONTAINER_MAX}px`}
        // 下に固定されるタブ & フッターを避けるため余白を確保
        pb={
          isBottom
            ? `${TAB_BAR_HEIGHT + FOOTER_HEIGHT + 20}px`
            : `${FOOTER_HEIGHT + 24}px`
        }
      >
        {/* タブバー / Tab bar */}
        <Box
          as="nav"
          // 下固定の場合のみ position: fixed（幅は中央固定＋max-widthで制限）
          position={isBottom ? "fixed" : "static"}
          bottom={isBottom ? 0 : undefined}
          left={isBottom ? "50%" : undefined}
          transform={isBottom ? "translateX(-50%)" : undefined}
          zIndex={10}
          bg="white"
          borderTop={isBottom ? "1px solid #eee" : undefined}
          borderBottom={!isBottom ? "1px solid #eee" : undefined}
          w={isBottom ? "100%" : "100%"}
          maxW={isBottom ? `${CONTAINER_MAX}px` : `${CONTAINER_MAX}px`}
          h={isBottom ? "auto" : undefined}
          minH={isBottom ? `${TAB_BAR_HEIGHT}px` : undefined}
          sx={isBottom ? { 
            paddingBottom: "env(safe-area-inset-bottom)",
            paddingTop: "4px",
          } : undefined}
          display="flex"
          justifyContent="space-around"
          alignItems="stretch"
        >
          {tabs.map((tab) => {
            const isActive = location.pathname === tab.path;
            return (
              <NavLink
                key={tab.key}
                to={tab.path}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isBottom ? "6px 4px" : "8px",
                  textDecoration: "none",
                  color: isActive ? "#3182ce" : "#4a5568",
                  borderBottom: !isBottom && isActive ? "2px solid #3182ce" : "none",
                  borderTop: isBottom && isActive ? "2px solid #3182ce" : "none",
                  backgroundColor: isActive ? "rgba(49, 130, 206, 0.1)" : "transparent",
                  minHeight: isBottom ? `${TAB_BAR_HEIGHT}px` : "auto",
                }}
              >
                <Icon as={tab.icon} boxSize={isBottom ? 4 : 5} />
                <Text 
                  fontSize={isBottom ? "xs" : "sm"} 
                  mt={isBottom ? 0.5 : 1}
                  textAlign="center"
                  lineHeight="tight"
                  noOfLines={2}
                  wordBreak="break-word"
                >
                  {tab.label}
                </Text>
              </NavLink>
            );
          })}
        </Box>

        {/* ルートコンテンツ / Route content */}
        <Box mt={isBottom ? 0 : 4}>
          {children}
        </Box>
      </Box>

      {/* フッター（常に一番下）。下タブがあるときはその直上に固定 */}
      <Box
        as="footer"
        bg="white"
        borderTop="1px solid #eee"
        h={`${FOOTER_HEIGHT}px`}
        display="flex"
        alignItems="center"
        justifyContent="center"
        px={4}
        w="100%"
        maxW={`${CONTAINER_MAX}px`}
        // md+ は自然な"最下部"（Flex 配置）。モバイルは fixed で下タブの直上に。
        position={isBottom ? "fixed" : "static"}
        left={isBottom ? "50%" : undefined}
        transform={isBottom ? "translateX(-50%)" : undefined}
        bottom={
          isBottom
            ? `calc(env(safe-area-inset-bottom) + ${TAB_BAR_HEIGHT}px + 4px)`
            : undefined
        }
        zIndex={9}
      >
        <Text fontSize="sm" color="gray.600">
          New to the Cyber Range?{" "}
          <Link
            href="https://www.skool.com/cyber-range/about?utm_source=tools-app&utm_medium=footer&utm_campaign=about-link"
            isExternal
            color="blue.500"
            textDecor="underline"
          >
            Learn more on Skool
          </Link>
          .
        </Text>
      </Box>
    </Flex>
  );
}
